/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  CONFIRM_PAYMENT_INSTANT: 'transition/confirm-payment-instant',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',
  // TASK045: Operator test shortcut — simulates 24h expiry immediately
  OPERATOR_TEST_EXPIRE_PREAUTHORIZED: 'transition/operator-test-expire-preauthorized',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',

  // TASK045: Customer cancels while waiting for provider to accept (full refund, no penalty)
  CUSTOMER_CANCEL_PENDING_BOOKING: 'transition/customer-cancel-pending-booking',
  
  // Customer cancellation transitions (time-based refund tiers)
  CANCEL_AFTER_ACCEPT_BY_CUSTOMER: 'transition/cancel-after-accept-by-customer', // Full refund (≥7 days)
  CANCEL_AFTER_ACCEPT_PARTIAL_REFUND_BY_CUSTOMER: 'transition/cancel-after-accept-partial-refund-by-customer', // 50% rental + 100% deposit (1-6 days)
  CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER: 'transition/cancel-after-accept-no-refund-by-customer', // Only deposit (0 days)
  
  // Operator cancellation transitions
  OPERATOR_CANCEL: 'transition/operator-cancel',
  OPERATOR_CANCEL_AFTER_REMINDER: 'transition/operator-cancel-after-reminder',
  OPERATOR_CANCEL_FROM_WAITING: 'transition/operator-cancel-from-waiting',
  OPERATOR_CANCEL_WITH_DEPOSIT: 'transition/operator-cancel-with-deposit',
  OPERATOR_CANCEL_DURING_CHECKIN: 'transition/operator-cancel-during-checkin',
  OPERATOR_CANCEL_DURING_RENTAL: 'transition/operator-cancel-during-rental',
  OPERATOR_CANCEL_DURING_CHECKOUT: 'transition/operator-cancel-during-checkout',
  OPERATOR_CANCEL_AFTER_CHECKOUT: 'transition/operator-cancel-after-checkout',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  // Check-in process transitions
  INITIATE_CHECK_IN: 'transition/initiate-check-in',
  INITIATE_CHECK_IN_WITH_PAYMENT: 'transition/initiate-check-in-with-payment',
  INITIATE_CHECK_IN_WITH_PAYMENT_FROM_REMINDER: 'transition/initiate-check-in-with-payment-from-reminder',
  UPLOAD_CHECK_IN_PHOTOS: 'transition/upload-check-in-photos',
  UPLOAD_CHECK_IN: 'transition/upload-check-in',
  COMPLETE_CHECK_IN: 'transition/complete-check-in',

  // Pre-checkin payment transitions
  REMIND_PAYMENT_METHOD: 'transition/remind-payment-method',
  CHARGE_BEFORE_CHECKIN: 'transition/charge-before-checkin',
  CONFIRM_CHARGE_SUCCESS: 'transition/confirm-charge-success',
  OPERATOR_TRIGGER_REMINDER: 'transition/operator-trigger-reminder',
  OPERATOR_TRIGGER_CHARGE: 'transition/operator-trigger-charge',
  CUSTOMER_TRIGGER_DEPOSIT_CHARGE: 'transition/customer-trigger-deposit-charge',
  CHARGE_BEFORE_CHECKIN_FROM_ACCEPTED: 'transition/charge-before-checkin-from-accepted',
  OPERATOR_TRIGGER_CHARGE_FROM_ACCEPTED: 'transition/operator-trigger-charge-from-accepted',
  INITIATE_CHECK_IN_FROM_WAITING: 'transition/initiate-check-in-from-waiting',

  // Check-out process transitions
  INITIATE_CHECK_OUT: 'transition/initiate-check-out',
  UPLOAD_CHECK_OUT_PHOTOS: 'transition/upload-check-out-photos',
  UPLOAD_CHECK_OUT: 'transition/upload-check-out',
  COMPLETE_CHECK_OUT: 'transition/complete-check-out',

  // TASK035: Unified Claim System - State-Based Workflow
  // Customer filing claims (moves to CLAIM_PENDING_RESPONSE state)
  CUSTOMER_FILE_CLAIM_FROM_CHECKIN_COMPLETED: 'transition/customer-file-claim-from-checkin-completed',
  CUSTOMER_FILE_CLAIM_FROM_CHECKOUT_INITIATED: 'transition/customer-file-claim-from-checkout-initiated',
  CUSTOMER_FILE_CLAIM_FROM_CHECKOUT_COMPLETED: 'transition/customer-file-claim-from-checkout-completed',

  // Provider filing claims (moves to CLAIM_PENDING_RESPONSE state)
  PROVIDER_FILE_CLAIM_FROM_CHECKIN_COMPLETED: 'transition/provider-file-claim-from-checkin-completed',
  PROVIDER_FILE_CLAIM_FROM_CHECKOUT_INITIATED: 'transition/provider-file-claim-from-checkout-initiated',
  PROVIDER_FILE_CLAIM_FROM_CHECKOUT_COMPLETED: 'transition/provider-file-claim-from-checkout-completed',

  // Response to claims (agree/dispute)
  CUSTOMER_AGREE_CLAIM: 'transition/customer-agree-claim',
  PROVIDER_AGREE_CLAIM: 'transition/provider-agree-claim',
  CUSTOMER_DISPUTE_CLAIM: 'transition/customer-dispute-claim',
  PROVIDER_DISPUTE_CLAIM: 'transition/provider-dispute-claim',

  // Auto-expire and operator resolution
  EXPIRE_CLAIM_RESPONSE: 'transition/expire-claim-response',
  OPERATOR_TEST_EXPIRE_CLAIM: 'transition/operator-test-expire-claim',
  OPERATOR_RESOLVE_CLAIM: 'transition/operator-resolve-claim',

  // Claim charge (after both parties agree)
  AUTO_CHARGE_CLAIM_DEPOSIT: 'transition/auto-charge-claim-deposit',
  OPERATOR_TRIGGER_CHARGE_CLAIM: 'transition/operator-trigger-charge-claim',
  COMPLETE_CLAIM_CHARGE: 'transition/complete-claim-charge',

  // Damage claim dispute flow transitions (TASK033f)
  PROVIDER_FILE_DAMAGE_CLAIM: 'transition/provider-file-damage-claim',
  CUSTOMER_AGREE_DAMAGE_CLAIM: 'transition/customer-agree-damage-claim',
  CUSTOMER_DISPUTE_DAMAGE_CLAIM: 'transition/customer-dispute-damage-claim',
  OPERATOR_RESOLVE_FOR_OWNER: 'transition/operator-resolve-for-owner',
  OPERATOR_RESOLVE_FOR_RENTER: 'transition/operator-resolve-for-renter',
  OPERATOR_PLATFORM_ABSORBS: 'transition/operator-platform-absorbs',
  EXPIRE_DAMAGE_CLAIM_RESPONSE: 'transition/expire-damage-claim-response',
  OPERATOR_TEST_EXPIRE_DAMAGE_CLAIM: 'transition/operator-test-expire-damage-claim',

  // Owner fault cancellation transitions (TASK033g)
  OPERATOR_CANCEL_OWNER_FAULT: 'transition/operator-cancel-owner-fault',
  OPERATOR_CANCEL_OWNER_FAULT_FROM_REMINDER: 'transition/operator-cancel-owner-fault-from-reminder',
  OPERATOR_CANCEL_OWNER_FAULT_FROM_PREPAID: 'transition/operator-cancel-owner-fault-from-prepaid',
  OPERATOR_CANCEL_OWNER_FAULT_FROM_CHECKIN: 'transition/operator-cancel-owner-fault-from-checkin',

  // Operator "time machine" shortcuts for testing (bypass waiting periods)
  OPERATOR_SKIP_DEPOSIT_TO_PREPAID: 'transition/operator-skip-deposit-to-prepaid',
  OPERATOR_COMPLETE_CHECKIN: 'transition/operator-complete-checkin',
  OPERATOR_SKIP_CHECKIN: 'transition/operator-skip-checkin',
  OPERATOR_INITIATE_CHECKOUT: 'transition/operator-initiate-checkout',
  OPERATOR_COMPLETE_CHECKOUT: 'transition/operator-complete-checkout',
  OPERATOR_SKIP_CHECKOUT: 'transition/operator-skip-checkout',
  // Fast-forward to delivered (booking-end passed, everything auto-resolved) → starts review period
  OPERATOR_FORCE_DELIVERED: 'transition/operator-force-delivered',

  // Deposit refund transition
  REFUND_DEPOSIT: 'transition/refund-deposit',
  OPERATOR_REFUND_DEPOSIT: 'transition/operator-refund-deposit',
  OPERATOR_COMPLETE_AFTER_CHECKOUT: 'transition/operator-complete-after-checkout',

  // Auto-release deposit transitions
  AUTO_RELEASE_DEPOSIT: 'transition/auto-release-deposit',
  OPERATOR_TEST_AUTO_RELEASE: 'transition/operator-test-auto-release',
  COMPLETE_AUTO_RELEASE: 'transition/complete-auto-release',

  // Pickup time change request transitions
  CUSTOMER_REQUEST_PICKUP_TIME_CHANGE: 'transition/customer-request-pickup-time-change',
  CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_REMINDER: 'transition/customer-request-pickup-time-change-from-reminder',
  CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_PREPAID: 'transition/customer-request-pickup-time-change-from-prepaid',
  CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_WAITING: 'transition/customer-request-pickup-time-change-from-waiting',
  PROVIDER_REQUEST_PICKUP_TIME_CHANGE: 'transition/provider-request-pickup-time-change',
  PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_REMINDER: 'transition/provider-request-pickup-time-change-from-reminder',
  PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_PREPAID: 'transition/provider-request-pickup-time-change-from-prepaid',
  PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_WAITING: 'transition/provider-request-pickup-time-change-from-waiting',
  ACCEPT_PICKUP_TIME_CHANGE: 'transition/accept-pickup-time-change',
  PROVIDER_ACCEPT_PICKUP_TIME_CHANGE: 'transition/provider-accept-pickup-time-change',
  DECLINE_PICKUP_TIME_CHANGE: 'transition/decline-pickup-time-change',
  PROVIDER_DECLINE_PICKUP_TIME_CHANGE: 'transition/provider-decline-pickup-time-change',
  EXPIRE_PICKUP_TIME_CHANGE: 'transition/expire-pickup-time-change',
  OPERATOR_EXPIRE_PICKUP_TIME_CHANGE: 'transition/operator-expire-pickup-time-change',

  // Extension request transitions
  CUSTOMER_REQUEST_EXTENSION: 'transition/customer-request-extension',
  PROVIDER_ACCEPT_EXTENSION: 'transition/provider-accept-extension',
  PROVIDER_DECLINE_EXTENSION: 'transition/provider-decline-extension',
  EXPIRE_EXTENSION_REQUEST: 'transition/expire-extension-request',
  OPERATOR_EXPIRE_EXTENSION: 'transition/operator-expire-extension',

  // Early return request transitions
  CUSTOMER_REQUEST_EARLY_RETURN: 'transition/customer-request-early-return',
  PROVIDER_ACCEPT_EARLY_RETURN: 'transition/provider-accept-early-return',
  PROVIDER_DECLINE_EARLY_RETURN: 'transition/provider-decline-early-return',
  EXPIRE_EARLY_RETURN_REQUEST: 'transition/expire-early-return-request',
  OPERATOR_EXPIRE_EARLY_RETURN: 'transition/operator-expire-early-return',

  // Late return escalation transitions (TASK033e)
  ESCALATE_LATE_RETURN_TIER1: 'transition/escalate-late-return-tier1',
  ESCALATE_LATE_RETURN_TIER2: 'transition/escalate-late-return-tier2',
  FILE_THEFT_CLAIM: 'transition/file-theft-claim',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  OPERATOR_EXPIRE_REVIEWS: 'transition/operator-expire-reviews',
  OPERATOR_EXPIRE_PROVIDER_REVIEW: 'transition/operator-expire-provider-review',
  OPERATOR_EXPIRE_CUSTOMER_REVIEW: 'transition/operator-expire-customer-review',

  // Renter no-show (TASK050 Phase 5). Report/respond go through /api/no-show/* (operator
  // transitions behind a role + time-window check), never directly from the client.
  // Renter cancellations after the booking left `accepted` (fired by /api/customer-cancel)
  CANCEL_AFTER_ACCEPT_BY_CUSTOMER_FROM_REMINDER: 'transition/cancel-after-accept-by-customer-from-reminder',
  CANCEL_AFTER_ACCEPT_BY_CUSTOMER_FROM_WAITING: 'transition/cancel-after-accept-by-customer-from-waiting',
  CANCEL_AFTER_ACCEPT_BY_CUSTOMER_FROM_PREPAID: 'transition/cancel-after-accept-by-customer-from-prepaid',
  CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER_FROM_REMINDER: 'transition/cancel-after-accept-no-refund-by-customer-from-reminder',
  CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER_FROM_WAITING: 'transition/cancel-after-accept-no-refund-by-customer-from-waiting',
  CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER_FROM_PREPAID: 'transition/cancel-after-accept-no-refund-by-customer-from-prepaid',

  REPORT_RENTER_NO_SHOW: 'transition/report-renter-no-show',
  REPORT_RENTER_NO_SHOW_FROM_REMINDER: 'transition/report-renter-no-show-from-reminder',
  REPORT_RENTER_NO_SHOW_FROM_WAITING: 'transition/report-renter-no-show-from-waiting',
  REPORT_RENTER_NO_SHOW_FROM_PREPAID: 'transition/report-renter-no-show-from-prepaid',
  RENTER_RESPOND_NO_SHOW: 'transition/renter-respond-no-show',
  CONFIRM_RENTER_NO_SHOW: 'transition/confirm-renter-no-show',
  OPERATOR_CONFIRM_RENTER_NO_SHOW: 'transition/operator-confirm-renter-no-show',
  OPERATOR_CONFIRM_RENTER_NO_SHOW_FROM_DISPUTED: 'transition/operator-confirm-renter-no-show-from-disputed',
  OPERATOR_RESOLVE_NO_SHOW_PICKUP_HAPPENED: 'transition/operator-resolve-no-show-pickup-happened',
  COMPLETE_RENTER_NO_SHOW: 'transition/complete-renter-no-show',
  RETRY_RENTER_NO_SHOW_SETTLEMENT: 'transition/retry-renter-no-show-settlement',
  COMPLETE_RENTER_NO_SHOW_AFTER_RETRY: 'transition/complete-renter-no-show-after-retry',
  AUTO_ASSUME_PICKUP: 'transition/auto-assume-pickup',
  AUTO_ASSUME_PICKUP_FROM_REMINDER: 'transition/auto-assume-pickup-from-reminder',
  AUTO_ASSUME_PICKUP_FROM_WAITING: 'transition/auto-assume-pickup-from-waiting',
  AUTO_ASSUME_PICKUP_FROM_PREPAID: 'transition/auto-assume-pickup-from-prepaid',
  INITIATE_CHECK_OUT_FROM_ASSUMED: 'transition/initiate-check-out-from-assumed',
  COMPLETE_ASSUMED_PICKUP: 'transition/complete-assumed-pickup',
  OPERATOR_COMPLETE_ASSUMED_PICKUP: 'transition/operator-complete-assumed-pickup',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  REMINDER_SENT: 'reminder-sent',
  PRE_PAID: 'pre-paid',
  WAITING_CHARGE_BEFORE_CHECKIN: 'waiting-charge-before-checkin',
  CHECK_IN_INITIATED: 'check-in-initiated',
  CHECK_IN_COMPLETED: 'check-in-completed',
  CHECK_OUT_INITIATED: 'check-out-initiated',
  CHECK_OUT_COMPLETED: 'check-out-completed',
  WAITING_AUTO_RELEASE: 'waiting-auto-release',
  PICKUP_TIME_CHANGE_PENDING: 'pickup-time-change-pending',
  EXTENSION_PENDING: 'extension-pending',
  EARLY_RETURN_PENDING: 'early-return-pending',
  DAMAGE_CLAIM_PENDING_RESPONSE: 'damage-claim-pending-response',
  DAMAGE_CLAIM_DISPUTED: 'damage-claim-disputed',
  CLAIM_PENDING_RESPONSE: 'claim-pending-response', // TASK035: Unified claim system
  CLAIM_DISPUTED: 'claim-disputed', // TASK035: Unified claim system
  CLAIM_AGREED: 'claim-agreed', // Both parties agreed; waiting for deposit charge
  WAITING_CLAIM_CHARGE: 'waiting-claim-charge', // Deposit partial capture in progress
  THEFT_CLAIMED: 'theft-claimed', // TASK033e: Theft claim filed after tier 3 escalation
  DEPOSIT_REFUNDED: 'deposit-refunded',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  CANCELLED_OWNER_FAULT: 'cancelled-owner-fault', // TASK033g: Owner fault cancellation
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  // Renter no-show (TASK050 Phase 5)
  RENTER_NO_SHOW_REPORTED: 'renter-no-show-reported',
  RENTER_NO_SHOW_DISPUTED: 'renter-no-show-disputed',
  RENTER_NO_SHOW_CONFIRMED: 'renter-no-show-confirmed',
  RENTER_NO_SHOW_SETTLEMENT_RETRY: 'renter-no-show-settlement-retry',
  RENTER_NO_SHOW_SETTLED: 'renter-no-show-settled',
  PICKUP_ASSUMED: 'pickup-assumed',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.CONFIRM_PAYMENT_INSTANT]: states.ACCEPTED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.OPERATOR_TEST_EXPIRE_PREAUTHORIZED]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        // TASK045: Customer can cancel freely while waiting for provider response
        [transitions.CUSTOMER_CANCEL_PENDING_BOOKING]: states.CANCELED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.REPORT_RENTER_NO_SHOW]: states.RENTER_NO_SHOW_REPORTED,
        [transitions.AUTO_ASSUME_PICKUP]: states.PICKUP_ASSUMED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.CANCEL_AFTER_ACCEPT_BY_CUSTOMER]: states.CANCELED,
        [transitions.CANCEL_AFTER_ACCEPT_PARTIAL_REFUND_BY_CUSTOMER]: states.CANCELED,
        [transitions.CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER]: states.CANCELED,
        [transitions.OPERATOR_CANCEL]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_OWNER_FAULT]: states.CANCELLED_OWNER_FAULT,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
        [transitions.REMIND_PAYMENT_METHOD]: states.REMINDER_SENT,
        [transitions.OPERATOR_TRIGGER_REMINDER]: states.REMINDER_SENT,
        [transitions.INITIATE_CHECK_IN_WITH_PAYMENT]: states.CHECK_IN_INITIATED,
        [transitions.CHARGE_BEFORE_CHECKIN_FROM_ACCEPTED]: states.WAITING_CHARGE_BEFORE_CHECKIN,
        [transitions.OPERATOR_TRIGGER_CHARGE_FROM_ACCEPTED]: states.WAITING_CHARGE_BEFORE_CHECKIN,
        [transitions.OPERATOR_SKIP_DEPOSIT_TO_PREPAID]: states.PRE_PAID,
        [transitions.CUSTOMER_REQUEST_PICKUP_TIME_CHANGE]: states.PICKUP_TIME_CHANGE_PENDING,
        [transitions.PROVIDER_REQUEST_PICKUP_TIME_CHANGE]: states.PICKUP_TIME_CHANGE_PENDING,
      },
    },

    [states.REMINDER_SENT]: {
      on: {
        [transitions.CANCEL_AFTER_ACCEPT_BY_CUSTOMER_FROM_REMINDER]: states.CANCELED,
        [transitions.CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER_FROM_REMINDER]: states.CANCELED,
        [transitions.REPORT_RENTER_NO_SHOW_FROM_REMINDER]: states.RENTER_NO_SHOW_REPORTED,
        [transitions.AUTO_ASSUME_PICKUP_FROM_REMINDER]: states.PICKUP_ASSUMED,
        [transitions.CHARGE_BEFORE_CHECKIN]: states.WAITING_CHARGE_BEFORE_CHECKIN,
        [transitions.CUSTOMER_TRIGGER_DEPOSIT_CHARGE]: states.WAITING_CHARGE_BEFORE_CHECKIN,
        [transitions.OPERATOR_TRIGGER_CHARGE]: states.WAITING_CHARGE_BEFORE_CHECKIN,
        [transitions.INITIATE_CHECK_IN_WITH_PAYMENT_FROM_REMINDER]: states.CHECK_IN_INITIATED,
        [transitions.OPERATOR_CANCEL_AFTER_REMINDER]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_OWNER_FAULT_FROM_REMINDER]: states.CANCELLED_OWNER_FAULT,
        [transitions.CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_REMINDER]: states.PICKUP_TIME_CHANGE_PENDING,
        [transitions.PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_REMINDER]: states.PICKUP_TIME_CHANGE_PENDING,
      },
    },

    [states.WAITING_CHARGE_BEFORE_CHECKIN]: {
      on: {
        [transitions.CANCEL_AFTER_ACCEPT_BY_CUSTOMER_FROM_WAITING]: states.CANCELED,
        [transitions.CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER_FROM_WAITING]: states.CANCELED,
        [transitions.REPORT_RENTER_NO_SHOW_FROM_WAITING]: states.RENTER_NO_SHOW_REPORTED,
        [transitions.AUTO_ASSUME_PICKUP_FROM_WAITING]: states.PICKUP_ASSUMED,
        [transitions.CONFIRM_CHARGE_SUCCESS]: states.PRE_PAID,
        [transitions.OPERATOR_CANCEL_FROM_WAITING]: states.CANCELED,
        [transitions.INITIATE_CHECK_IN_FROM_WAITING]: states.CHECK_IN_INITIATED,
        [transitions.CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_WAITING]: states.PICKUP_TIME_CHANGE_PENDING,
        [transitions.PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_WAITING]: states.PICKUP_TIME_CHANGE_PENDING,
      },
    },

    [states.PRE_PAID]: {
      on: {
        [transitions.CANCEL_AFTER_ACCEPT_BY_CUSTOMER_FROM_PREPAID]: states.CANCELED,
        [transitions.CANCEL_AFTER_ACCEPT_NO_REFUND_BY_CUSTOMER_FROM_PREPAID]: states.CANCELED,
        [transitions.REPORT_RENTER_NO_SHOW_FROM_PREPAID]: states.RENTER_NO_SHOW_REPORTED,
        [transitions.AUTO_ASSUME_PICKUP_FROM_PREPAID]: states.PICKUP_ASSUMED,
        [transitions.OPERATOR_CANCEL_WITH_DEPOSIT]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_OWNER_FAULT_FROM_PREPAID]: states.CANCELLED_OWNER_FAULT,
        [transitions.INITIATE_CHECK_IN]: states.CHECK_IN_INITIATED,
        [transitions.OPERATOR_SKIP_CHECKIN]: states.CHECK_IN_COMPLETED,
        [transitions.CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_PREPAID]: states.PICKUP_TIME_CHANGE_PENDING,
        [transitions.PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_PREPAID]: states.PICKUP_TIME_CHANGE_PENDING,
      },
    },

    [states.CHECK_IN_INITIATED]: {
      on: {
        [transitions.UPLOAD_CHECK_IN_PHOTOS]: states.CHECK_IN_INITIATED,
        [transitions.UPLOAD_CHECK_IN]: states.CHECK_IN_INITIATED,
        [transitions.OPERATOR_CANCEL_DURING_CHECKIN]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_OWNER_FAULT_FROM_CHECKIN]: states.CANCELLED_OWNER_FAULT,
        [transitions.COMPLETE_CHECK_IN]: states.CHECK_IN_COMPLETED,
        [transitions.OPERATOR_COMPLETE_CHECKIN]: states.CHECK_IN_COMPLETED,
      },
    },

    [states.CHECK_IN_COMPLETED]: {
      on: {
        // TASK035: New state-based claim transitions
        [transitions.CUSTOMER_FILE_CLAIM_FROM_CHECKIN_COMPLETED]: states.CLAIM_PENDING_RESPONSE,
        [transitions.PROVIDER_FILE_CLAIM_FROM_CHECKIN_COMPLETED]: states.CLAIM_PENDING_RESPONSE,
        [transitions.OPERATOR_CANCEL_DURING_RENTAL]: states.CANCELED,
        [transitions.CUSTOMER_REQUEST_EXTENSION]: states.EXTENSION_PENDING,
        [transitions.CUSTOMER_REQUEST_EARLY_RETURN]: states.EARLY_RETURN_PENDING,
        [transitions.INITIATE_CHECK_OUT]: states.CHECK_OUT_INITIATED,
        [transitions.OPERATOR_SKIP_CHECKOUT]: states.CHECK_OUT_COMPLETED,
        // TASK033e: Late return escalation (self-loops stay in check-in-completed)
        [transitions.ESCALATE_LATE_RETURN_TIER1]: states.CHECK_IN_COMPLETED,
        [transitions.ESCALATE_LATE_RETURN_TIER2]: states.CHECK_IN_COMPLETED,
        [transitions.FILE_THEFT_CLAIM]: states.THEFT_CLAIMED,
      },
    },

    [states.THEFT_CLAIMED]: {},

    [states.CHECK_OUT_INITIATED]: {
      on: {
        // TASK035: New state-based claim transitions
        [transitions.CUSTOMER_FILE_CLAIM_FROM_CHECKOUT_INITIATED]: states.CLAIM_PENDING_RESPONSE,
        [transitions.PROVIDER_FILE_CLAIM_FROM_CHECKOUT_INITIATED]: states.CLAIM_PENDING_RESPONSE,
        [transitions.UPLOAD_CHECK_OUT_PHOTOS]: states.CHECK_OUT_INITIATED,
        [transitions.UPLOAD_CHECK_OUT]: states.CHECK_OUT_INITIATED,
        [transitions.OPERATOR_CANCEL_DURING_CHECKOUT]: states.CANCELED,
        [transitions.COMPLETE_CHECK_OUT]: states.CHECK_OUT_COMPLETED,
      },
    },

    [states.CHECK_OUT_COMPLETED]: {
      on: {
        // TASK035: New state-based claim transitions
        [transitions.CUSTOMER_FILE_CLAIM_FROM_CHECKOUT_COMPLETED]: states.CLAIM_PENDING_RESPONSE,
        [transitions.PROVIDER_FILE_CLAIM_FROM_CHECKOUT_COMPLETED]: states.CLAIM_PENDING_RESPONSE,
        [transitions.PROVIDER_FILE_DAMAGE_CLAIM]: states.DAMAGE_CLAIM_PENDING_RESPONSE,
        [transitions.REFUND_DEPOSIT]: states.DEPOSIT_REFUNDED,
        [transitions.OPERATOR_REFUND_DEPOSIT]: states.DEPOSIT_REFUNDED,
        [transitions.AUTO_RELEASE_DEPOSIT]: states.WAITING_AUTO_RELEASE,
        [transitions.OPERATOR_TEST_AUTO_RELEASE]: states.WAITING_AUTO_RELEASE,
        [transitions.OPERATOR_CANCEL_AFTER_CHECKOUT]: states.CANCELED,
        [transitions.OPERATOR_COMPLETE_AFTER_CHECKOUT]: states.DELIVERED,
      },
    },

    // TASK035: Unified claim system states
    [states.CLAIM_PENDING_RESPONSE]: {
      on: {
        [transitions.CUSTOMER_AGREE_CLAIM]: states.CLAIM_AGREED,
        [transitions.PROVIDER_AGREE_CLAIM]: states.CLAIM_AGREED,
        [transitions.CUSTOMER_DISPUTE_CLAIM]: states.CLAIM_DISPUTED,
        [transitions.PROVIDER_DISPUTE_CLAIM]: states.CLAIM_DISPUTED,
        [transitions.EXPIRE_CLAIM_RESPONSE]: states.CLAIM_DISPUTED,
        [transitions.OPERATOR_TEST_EXPIRE_CLAIM]: states.CLAIM_DISPUTED,
      },
    },

    [states.CLAIM_AGREED]: {
      on: {
        [transitions.AUTO_CHARGE_CLAIM_DEPOSIT]: states.WAITING_CLAIM_CHARGE,
        [transitions.OPERATOR_TRIGGER_CHARGE_CLAIM]: states.WAITING_CLAIM_CHARGE,
      },
    },

    [states.WAITING_CLAIM_CHARGE]: {
      on: {
        [transitions.COMPLETE_CLAIM_CHARGE]: states.CHECK_OUT_COMPLETED,
      },
    },

    [states.CLAIM_DISPUTED]: {
      on: {
        [transitions.OPERATOR_RESOLVE_CLAIM]: states.CHECK_OUT_COMPLETED,
      },
    },

    [states.DAMAGE_CLAIM_PENDING_RESPONSE]: {
      on: {
        [transitions.CUSTOMER_AGREE_DAMAGE_CLAIM]: states.CHECK_OUT_COMPLETED,
        [transitions.CUSTOMER_DISPUTE_DAMAGE_CLAIM]: states.DAMAGE_CLAIM_DISPUTED,
        [transitions.EXPIRE_DAMAGE_CLAIM_RESPONSE]: states.DAMAGE_CLAIM_DISPUTED,
        [transitions.OPERATOR_TEST_EXPIRE_DAMAGE_CLAIM]: states.DAMAGE_CLAIM_DISPUTED,
      },
    },

    [states.DAMAGE_CLAIM_DISPUTED]: {
      on: {
        [transitions.OPERATOR_RESOLVE_FOR_OWNER]: states.CHECK_OUT_COMPLETED,
        [transitions.OPERATOR_RESOLVE_FOR_RENTER]: states.CHECK_OUT_COMPLETED,
        [transitions.OPERATOR_PLATFORM_ABSORBS]: states.CHECK_OUT_COMPLETED,
      },
    },

    [states.WAITING_AUTO_RELEASE]: {
      on: {
        [transitions.COMPLETE_AUTO_RELEASE]: states.DEPOSIT_REFUNDED,
      },
    },

    [states.PICKUP_TIME_CHANGE_PENDING]: {
      on: {
        [transitions.ACCEPT_PICKUP_TIME_CHANGE]: states.ACCEPTED,
        [transitions.PROVIDER_ACCEPT_PICKUP_TIME_CHANGE]: states.ACCEPTED,
        [transitions.DECLINE_PICKUP_TIME_CHANGE]: states.ACCEPTED,
        [transitions.PROVIDER_DECLINE_PICKUP_TIME_CHANGE]: states.ACCEPTED,
        [transitions.EXPIRE_PICKUP_TIME_CHANGE]: states.ACCEPTED,
      },
    },

    [states.EXTENSION_PENDING]: {
      on: {
        [transitions.PROVIDER_ACCEPT_EXTENSION]: states.CHECK_IN_COMPLETED,
        [transitions.PROVIDER_DECLINE_EXTENSION]: states.CHECK_IN_COMPLETED,
        [transitions.EXPIRE_EXTENSION_REQUEST]: states.CHECK_IN_COMPLETED,
      },
    },

    [states.EARLY_RETURN_PENDING]: {
      on: {
        [transitions.PROVIDER_ACCEPT_EARLY_RETURN]: states.CHECK_IN_COMPLETED,
        [transitions.PROVIDER_DECLINE_EARLY_RETURN]: states.CHECK_IN_COMPLETED,
        [transitions.EXPIRE_EARLY_RETURN_REQUEST]: states.CHECK_IN_COMPLETED,
      },
    },

    [states.DEPOSIT_REFUNDED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
      },
    },

    [states.CANCELED]: {},
    [states.CANCELLED_OWNER_FAULT]: {}, // TASK033g: Owner fault cancellation (terminal state)

    // Renter no-show (TASK050 Phase 5)
    [states.RENTER_NO_SHOW_REPORTED]: {
      on: {
        [transitions.RENTER_RESPOND_NO_SHOW]: states.RENTER_NO_SHOW_DISPUTED,
        [transitions.CONFIRM_RENTER_NO_SHOW]: states.RENTER_NO_SHOW_CONFIRMED,
        [transitions.OPERATOR_CONFIRM_RENTER_NO_SHOW]: states.RENTER_NO_SHOW_CONFIRMED,
      },
    },
    [states.RENTER_NO_SHOW_DISPUTED]: {
      on: {
        [transitions.OPERATOR_CONFIRM_RENTER_NO_SHOW_FROM_DISPUTED]: states.RENTER_NO_SHOW_CONFIRMED,
        [transitions.OPERATOR_RESOLVE_NO_SHOW_PICKUP_HAPPENED]: states.PICKUP_ASSUMED,
      },
    },
    [states.RENTER_NO_SHOW_CONFIRMED]: {
      on: {
        [transitions.COMPLETE_RENTER_NO_SHOW]: states.RENTER_NO_SHOW_SETTLED,
        [transitions.RETRY_RENTER_NO_SHOW_SETTLEMENT]: states.RENTER_NO_SHOW_SETTLEMENT_RETRY,
      },
    },
    [states.RENTER_NO_SHOW_SETTLEMENT_RETRY]: {
      on: {
        [transitions.COMPLETE_RENTER_NO_SHOW_AFTER_RETRY]: states.RENTER_NO_SHOW_SETTLED,
      },
    },
    [states.RENTER_NO_SHOW_SETTLED]: { type: 'final' },
    [states.PICKUP_ASSUMED]: {
      on: {
        [transitions.INITIATE_CHECK_OUT_FROM_ASSUMED]: states.CHECK_OUT_INITIATED,
        [transitions.COMPLETE_ASSUMED_PICKUP]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_ASSUMED_PICKUP]: states.DELIVERED,
      },
    },
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.OPERATOR_CANCEL,
    transitions.OPERATOR_CANCEL_AFTER_REMINDER,
    transitions.OPERATOR_CANCEL_FROM_WAITING,
    transitions.OPERATOR_CANCEL_WITH_DEPOSIT,
    transitions.OPERATOR_CANCEL_DURING_CHECKIN,
    transitions.OPERATOR_CANCEL_DURING_RENTAL,
    transitions.OPERATOR_CANCEL_DURING_CHECKOUT,
    transitions.OPERATOR_CANCEL_AFTER_CHECKOUT,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_INSTANT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    // TASK035: Unified claim system transitions (state-based)
    transitions.CUSTOMER_FILE_CLAIM_FROM_CHECKIN_COMPLETED,
    transitions.CUSTOMER_FILE_CLAIM_FROM_CHECKOUT_INITIATED,
    transitions.CUSTOMER_FILE_CLAIM_FROM_CHECKOUT_COMPLETED,
    transitions.PROVIDER_FILE_CLAIM_FROM_CHECKIN_COMPLETED,
    transitions.PROVIDER_FILE_CLAIM_FROM_CHECKOUT_INITIATED,
    transitions.PROVIDER_FILE_CLAIM_FROM_CHECKOUT_COMPLETED,
    transitions.CUSTOMER_AGREE_CLAIM,
    transitions.PROVIDER_AGREE_CLAIM,
    transitions.CUSTOMER_DISPUTE_CLAIM,
    transitions.PROVIDER_DISPUTE_CLAIM,
    transitions.EXPIRE_CLAIM_RESPONSE,
    transitions.OPERATOR_RESOLVE_CLAIM,
    transitions.CUSTOMER_REQUEST_PICKUP_TIME_CHANGE,
    transitions.CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_REMINDER,
    transitions.CUSTOMER_REQUEST_PICKUP_TIME_CHANGE_FROM_PREPAID,
    transitions.PROVIDER_REQUEST_PICKUP_TIME_CHANGE,
    transitions.PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_REMINDER,
    transitions.PROVIDER_REQUEST_PICKUP_TIME_CHANGE_FROM_PREPAID,
    transitions.ACCEPT_PICKUP_TIME_CHANGE,
    transitions.PROVIDER_ACCEPT_PICKUP_TIME_CHANGE,
    transitions.DECLINE_PICKUP_TIME_CHANGE,
    transitions.PROVIDER_DECLINE_PICKUP_TIME_CHANGE,
    transitions.EXPIRE_PICKUP_TIME_CHANGE,
    transitions.INITIATE_CHECK_IN,
    transitions.INITIATE_CHECK_IN_WITH_PAYMENT,
    transitions.INITIATE_CHECK_IN_WITH_PAYMENT_FROM_REMINDER,
    transitions.INITIATE_CHECK_IN_FROM_WAITING,
    transitions.COMPLETE_CHECK_IN,
    transitions.INITIATE_CHECK_OUT,
    transitions.COMPLETE_CHECK_OUT,
    transitions.CUSTOMER_REQUEST_EXTENSION,
    transitions.PROVIDER_ACCEPT_EXTENSION,
    transitions.PROVIDER_DECLINE_EXTENSION,
    transitions.EXPIRE_EXTENSION_REQUEST,
    transitions.REFUND_DEPOSIT,
    transitions.OPERATOR_REFUND_DEPOSIT,
    transitions.AUTO_RELEASE_DEPOSIT,
    transitions.OPERATOR_TEST_AUTO_RELEASE,
    transitions.COMPLETE_AUTO_RELEASE,
    // Deposit charge flow transitions
    transitions.REMIND_PAYMENT_METHOD,
    transitions.OPERATOR_TRIGGER_REMINDER,
    transitions.CHARGE_BEFORE_CHECKIN,
    transitions.CHARGE_BEFORE_CHECKIN_FROM_ACCEPTED,
    transitions.CUSTOMER_TRIGGER_DEPOSIT_CHARGE,
    transitions.CONFIRM_CHARGE_SUCCESS,
    // TASK033g: Owner fault cancellation
    transitions.OPERATOR_CANCEL_OWNER_FAULT,
    transitions.OPERATOR_CANCEL_OWNER_FAULT_FROM_REMINDER,
    transitions.OPERATOR_CANCEL_OWNER_FAULT_FROM_PREPAID,
    transitions.OPERATOR_CANCEL_OWNER_FAULT_FROM_CHECKIN,
    // TASK050: Renter no-show
    transitions.REPORT_RENTER_NO_SHOW,
    transitions.REPORT_RENTER_NO_SHOW_FROM_REMINDER,
    transitions.REPORT_RENTER_NO_SHOW_FROM_WAITING,
    transitions.REPORT_RENTER_NO_SHOW_FROM_PREPAID,
    transitions.RENTER_RESPOND_NO_SHOW,
    transitions.CONFIRM_RENTER_NO_SHOW,
    transitions.OPERATOR_CONFIRM_RENTER_NO_SHOW,
    transitions.OPERATOR_CONFIRM_RENTER_NO_SHOW_FROM_DISPUTED,
    transitions.OPERATOR_RESOLVE_NO_SHOW_PICKUP_HAPPENED,
    transitions.COMPLETE_RENTER_NO_SHOW,
    transitions.COMPLETE_RENTER_NO_SHOW_AFTER_RETRY,
    transitions.AUTO_ASSUME_PICKUP,
    transitions.AUTO_ASSUME_PICKUP_FROM_REMINDER,
    transitions.AUTO_ASSUME_PICKUP_FROM_WAITING,
    transitions.AUTO_ASSUME_PICKUP_FROM_PREPAID,
    transitions.INITIATE_CHECK_OUT_FROM_ASSUMED,
    transitions.COMPLETE_ASSUMED_PICKUP,
    transitions.OPERATOR_COMPLETE_ASSUMED_PICKUP,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY].includes(
    transition
  );
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
    transitions.COMPLETE_ASSUMED_PICKUP,
    transitions.OPERATOR_COMPLETE_ASSUMED_PICKUP,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.OPERATOR_CANCEL,
    transitions.OPERATOR_CANCEL_AFTER_REMINDER,
    transitions.OPERATOR_CANCEL_FROM_WAITING,
    transitions.OPERATOR_CANCEL_WITH_DEPOSIT,
    transitions.OPERATOR_CANCEL_DURING_CHECKIN,
    transitions.OPERATOR_CANCEL_DURING_RENTAL,
    transitions.OPERATOR_CANCEL_DURING_CHECKOUT,
    transitions.OPERATOR_CANCEL_AFTER_CHECKOUT,
    transitions.DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];

export const statesNeedingCustomerAttention = [
  states.ACCEPTED,           // Booking was accepted - customer should see notification
  states.DECLINED,           // Booking was declined - customer should see notification  
  states.REMINDER_SENT,      // Payment method needed - action required
  states.CHECK_IN_INITIATED, // Check-in started - action required
  states.CHECK_OUT_INITIATED,// Check-out started - action required
  states.RENTER_NO_SHOW_REPORTED, // Owner reported a no-show - renter has 24h to respond
];
